// pages/WelcomePage.tsx
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Sparkles,
  Target,
  Users,
  Building2,
  BarChart3,
  ArrowRight,
  Calendar,
  TrendingUp,
  FileText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function WelcomePage() {
  const { user, can, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const getUserGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Buenos días";
    if (hour < 19) return "Buenas tardes";
    return "Buenas noches";
  };

  const formatDate = () => {
    return new Date().toLocaleDateString("es-ES", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const quickActions = [
    {
      title: "Contactos",
      description: "Gestiona tu base de clientes",
      icon: Users,
      href: "/contacts",
      requiredPermission: "contacts.read",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    {
      title: "Oportunidades",
      description: "Seguimiento de ventas",
      icon: Target,
      href: "/opportunities",
      requiredPermission: "opportunities.read",
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      title: "Empresas",
      description: "Cartera de empresas",
      icon: Building2,
      href: "/companies",
      requiredPermission: "client_companies.read",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    {
      title: "Dashboards",
      description: "Métricas y análisis",
      icon: BarChart3,
      href: "/reports",
      requiredPermission: "reports.view",
      color: "text-orange-600",
      bgColor: "bg-orange-100",
    },
  ];

  const filteredActions = quickActions.filter(
    (action) => !action.requiredPermission || can(action.requiredPermission),
  );

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <div className="space-y-8 max-w-md">
          <h1 className="text-5xl font-bold text-gray-900">
            Bienvenido a Prographic
          </h1>
          <p className="text-lg text-gray-600">
            Sistema Prographic profesional para gestión de ventas y clientes
          </p>
          <Button size="lg" className="mt-4" onClick={() => navigate("/login")}>
            Iniciar Sesión
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 p-6">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 to-slate-800 p-8 md:p-12 text-white shadow-2xl">
          <div className="relative z-10">
            <div className="flex items-center gap-2 text-slate-300 mb-4">
              <Calendar className="h-4 w-4" />
              <span className="text-sm font-medium capitalize">
                {formatDate()}
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight">
              {getUserGreeting()},{" "}
              <span className="text-blue-400">
                {user?.first_name || user?.name || "Usuario"}
              </span>
            </h1>
            <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">
              Bienvenido a tu espacio de trabajo. Aquí tienes todo lo necesario
              para gestionar tus relaciones comerciales y potenciar tus ventas.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                onClick={() => navigate("/opportunities")}
                className="bg-blue-600 hover:bg-blue-700 text-white border-none shadow-lg shadow-blue-900/20"
              >
                Ver Pipeline
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/contacts")}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
              >
                Buscar Contacto
              </Button>
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute top-0 right-0 -mt-20 -mr-20 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 -mb-20 -ml-20 h-80 w-80 rounded-full bg-purple-600/20 blur-3xl"></div>
        </div>

        {/* Quick Actions Grid */}
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" />
            Acceso Rápido
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredActions.map((action, index) => (
              <Card
                key={index}
                className="group cursor-pointer border-slate-200 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-900/5 transition-all duration-300 overflow-hidden"
                onClick={() => navigate(action.href)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className={`p-3 rounded-2xl ${action.bgColor} ${action.color} group-hover:scale-110 transition-transform duration-300`}
                    >
                      <action.icon className="h-6 w-6" />
                    </div>
                    <div className="h-8 w-8 rounded-full bg-slate-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 -mr-2 -mt-2">
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                  <h3 className="font-bold text-lg text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-sm text-slate-500">{action.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Info / Tips Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="bg-gradient-to-br from-white to-slate-50 border-slate-200">
            <CardContent className="p-8 flex items-center gap-6">
              <div className="p-4 bg-indigo-100 text-indigo-600 rounded-2xl">
                <TrendingUp className="h-8 w-8" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Impulsa tus Ventas
                </h3>
                <p className="text-slate-600 mb-4">
                  Mantén un seguimiento constante de tus oportunidades para
                  aumentar tu tasa de cierre.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-white to-slate-50 border-slate-200">
            <CardContent className="p-8 flex items-center gap-6">
              <div className="p-4 bg-emerald-100 text-emerald-600 rounded-2xl">
                <FileText className="h-8 w-8" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Organización Total
                </h3>
                <p className="text-slate-600 mb-4">
                  Centraliza toda la información de tus clientes y empresas en
                  un solo lugar seguro.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
